import React, { useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { NAV } from "../Navbar/navbarTheme";
import {
  bulletListSx,
  listItemSx,
  pageContentSx,
  pageGutterSx,
  pageShellSx,
  pageTitleSx,
  sectionBodySx,
  sectionHeadingSx,
  tableBodyCellSx,
  tableHeaderCellSx,
} from "./whatWeOfferStyles";

const headOfficeRows = [
  { range: "500kg or more", card: "£1.10", cash: "£1.15" },
  { range: "150kg to 499kg", card: "£1.15", cash: "£1.20" },
  { range: "50kg to 149kg", card: "£1.20", cash: "£1.25" },
  { range: "1kg to 49kg", card: "£1.25", cash: "£1.30" },
];

const agentOfficeRows = [
  { range: "500kg or more", card: "£1.15", cash: "£1.20" },
  { range: "150kg to 499kg", card: "£1.20", cash: "£1.25" },
  { range: "50kg to 149kg", card: "£1.25", cash: "£1.30" },
  { range: "1kg to 49kg", card: "£1.30", cash: "£1.35" },
];

const collectionRows = [
  { weight: "More than 150Kg", fee: "FREE" },
  { weight: "Less than 150Kg", fee: "£15" },
];

const noteItems = [
  "Above 150 Kg: Free collection at your doorstep, charged at the Head Office price ranges.",
  "Below 150 Kg: A £10 delivery charge will apply, charged at the Agent's price ranges.",
];

function PricingTable({ columns, rows }) {
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        mb: { xs: 3, md: 4 },
        border: `1px solid ${NAV.line}`,
        borderRadius: 2.5,
        overflow: "hidden",
        boxShadow: "0 4px 18px rgba(15, 40, 71, 0.06)",
      }}
    >
      <Table sx={{ minWidth: 280 }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.key} align="center" sx={tableHeaderCellSx}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={row.range || row.weight}
              sx={{
                bgcolor: index % 2 === 0 ? "#fff" : NAV.surfaceSubtle,
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align="center"
                  sx={tableBodyCellSx}
                  component={column.key === columns[0].key ? "th" : "td"}
                  scope={column.key === columns[0].key ? "row" : undefined}
                >
                  {row[column.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function NoteList({ items }) {
  return (
    <List dense sx={bulletListSx}>
      {items.map((item) => (
        <ListItem key={item} sx={listItemSx}>
          <ListItemText
            primary={item}
            primaryTypographyProps={{
              sx: {
                fontSize: { xs: "0.9375rem", md: "1.0625rem" },
                lineHeight: 1.7,
              },
            }}
          />
        </ListItem>
      ))}
    </List>
  );
}

const Payment = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Box component="main" sx={pageShellSx}>
      <Box sx={pageGutterSx}>
        <Box sx={pageContentSx}>
          <Typography component="h1" sx={pageTitleSx}>
            Pricing List
          </Typography>

          <Typography sx={{ ...sectionBodySx, maxWidth: 900 }}>
            At{" "}
            <Box component="span" sx={{ color: "#1e5a9e", fontWeight: 700 }}>
              UK TO MARS
            </Box>
            , we offer transparent and competitive pricing to meet your shipping
            needs. Whether you&apos;re transporting goods for business or personal
            use, our pricing structure ensures flexibility, simplicity, and
            value.
          </Typography>

          <Typography component="h2" sx={sectionHeadingSx}>
            1. Receiving Goods at the UK Head Office
          </Typography>
          <PricingTable
            columns={[
              { key: "range", label: "Weight Range" },
              { key: "card", label: "Price (Card)" },
              { key: "cash", label: "Price (Cash)" },
            ]}
            rows={headOfficeRows}
          />

          <Typography component="h2" sx={sectionHeadingSx}>
            2. Receiving Goods at the Agent&apos;s Office
          </Typography>
          <PricingTable
            columns={[
              { key: "range", label: "Weight Range" },
              { key: "card", label: "Price (Card)" },
              { key: "cash", label: "Price (Cash)" },
            ]}
            rows={agentOfficeRows}
          />

          <Typography component="h2" sx={sectionHeadingSx}>
            3. Collection at Your Doorstep
          </Typography>
          <PricingTable
            columns={[
              { key: "weight", label: "Weight (Kg)" },
              { key: "fee", label: "Collection Fee" },
            ]}
            rows={collectionRows}
          />

          <Typography component="h2" sx={sectionHeadingSx}>
            Note
          </Typography>
          <NoteList items={noteItems} />

          <Box
            sx={{
              mt: { xs: 3, md: 4 },
              pt: { xs: 3, md: 4 },
              borderTop: `1px solid ${NAV.line}`,
            }}
          >
            <Typography component="h2" sx={sectionHeadingSx}>
              Attention
            </Typography>
            <Typography sx={sectionBodySx}>
              When sending your items or using our office address, please write
              the company name exactly as shown below to ensure efficient and
              safe delivery of your goods.
            </Typography>

            <Typography component="h2" sx={{ ...sectionHeadingSx, mt: 3 }}>
              UK Head Office Address
            </Typography>
            <Typography sx={sectionBodySx}>UK TO MARS LTD</Typography>
            <Typography sx={{ ...sectionBodySx, mb: 0 }}>
              15 Little Balmer, Buckingham Industrial Estate, Buckingham, MK18
              1TF, United Kingdom
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Payment;
